import { useEffect, useMemo, useState } from "react";
import { ref, onValue } from "firebase/database";
import { database } from "../services/firebase";
import { useAuth } from "../context/AuthContext";

function snapshotToArray(data) {
  if (!data) {
    return [];
  }
  return Object.entries(data)
    .map(([id, value]) => ({ id, ...value }))
    .sort((a, b) => (b.created_at || 0) - (a.created_at || 0));
}

export function useDataStreams() {
  const { currentUser } = useAuth();
  const [seeds, setSeeds] = useState([]);
  const [projects, setProjects] = useState([]);
  const [versions, setVersions] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!currentUser) {
      setSeeds([]);
      setProjects([]);
      setVersions([]);
      setVideos([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    const status = { seeds: false, projects: false, versions: false, videos: false };
    const markLoaded = (key) => {
      status[key] = true;
      if (status.seeds && status.projects && status.versions && status.videos) {
        setLoading(false);
      }
    };

    const seedsRef = ref(database, `user_data/${currentUser.uid}/seeds`);
    const projectsRef = ref(database, `user_data/${currentUser.uid}/projects`);
    const versionsRef = ref(database, `user_data/${currentUser.uid}/versions`);
    const videosRef = ref(database, `user_data/${currentUser.uid}/videos`);

    const unsubSeeds = onValue(
      seedsRef,
      (snapshot) => {
        try {
          setSeeds(snapshotToArray(snapshot.val()));
          setError(null);
        } catch (err) {
          setError(err.message);
        } finally {
          markLoaded("seeds");
        }
      },
      (err) => {
        setError(err.message);
        markLoaded("seeds");
      }
    );

    const unsubProjects = onValue(
      projectsRef,
      (snapshot) => {
        try {
          const formatted = snapshotToArray(snapshot.val()).map((project) => ({
            ...project,
            state: project.state || "pending",
          }));
          setProjects(formatted);
          setError(null);
        } catch (err) {
          setError(err.message);
        } finally {
          markLoaded("projects");
        }
      },
      (err) => {
        setError(err.message);
        markLoaded("projects");
      }
    );

    const unsubVersions = onValue(
      versionsRef,
      (snapshot) => {
        try {
          const formatted = snapshotToArray(snapshot.val()).map((version) => ({
            ...version,
            state: version.state || "pending",
          }));
          setVersions(formatted);
          setError(null);
        } catch (err) {
          setError(err.message);
        } finally {
          markLoaded("versions");
        }
      },
      (err) => {
        setError(err.message);
        markLoaded("versions");
      }
    );

    const unsubVideos = onValue(
      videosRef,
      (snapshot) => {
        try {
          setVideos(snapshotToArray(snapshot.val()));
          setError(null);
        } catch (err) {
          setError(err.message);
        } finally {
          markLoaded("videos");
        }
      },
      (err) => {
        setError(err.message);
        markLoaded("videos");
      }
    );

    return () => {
      unsubSeeds();
      unsubProjects();
      unsubVersions();
      unsubVideos();
    };
  }, [currentUser]);

  const projectsWithVersions = useMemo(() => {
    const map = new Map();
    projects.forEach((proj) => {
      map.set(proj.id, { ...proj, versions: [], updated_at: proj.updated_at || 0 });
    });

    versions.forEach((version) => {
      const parent = map.get(version.project_id);
      const entry = {
        ...version,
        state: version.state || "pending",
      };
      if (parent) {
        parent.versions.push(entry);
        const latestUpdated = Math.max(parent.updated_at || 0, entry.updated_at || 0);
        parent.updated_at = latestUpdated;
        if (!parent.cover_image_url) {
          parent.cover_image_url =
            entry.current_image_url || entry.source_image_url || parent.cover_image_url || null;
        }
      }
    });

    return Array.from(map.values()).map((proj) => ({
      ...proj,
      versions: (proj.versions || []).sort(
        (a, b) => (b.created_at || b.updated_at || 0) - (a.created_at || a.updated_at || 0)
      ),
    }));
  }, [projects, versions]);

  const sortedSeeds = useMemo(() => {
    return [...seeds].sort((a, b) => {
      const aStar = a.starred ? 1 : 0;
      const bStar = b.starred ? 1 : 0;
      if (aStar !== bStar) return bStar - aStar;
      return (b.created_at || 0) - (a.created_at || 0);
    });
  }, [seeds]);

  return {
    seeds: sortedSeeds,
    projects: projectsWithVersions,
    versions,
    videos,
    loading,
    error,
  };
}
